import * as React from 'react'
import { InfoWindow } from '@react-google-maps/api'

import { useTheme } from '@thg-commerce/gravity-patterns/theme'

import { Store } from '../StoreDetails/StoreDetails'
import {
  I18nText as StoreDetailsCardI18nText,
  StoreDetailsCard,
} from '../StoreDetailsCard/StoreDetailsCard'

import { MapLocationProps, MarkerProps, PinProps } from './MapTypes'
import { openGoogleDirectionMap } from './utils'

const PinDetailsCard = ({
  searchNo,
  position,
  store,
  storeDetailsHref,
  cardText,
}: {
  searchNo: number
  position: MapLocationProps
  store: Store
  storeDetailsHref?: (
    region: string,
    town: string,
    storeUrlTag: string,
  ) => string
  cardText: StoreDetailsCardI18nText
}) => {
  return (
    <StoreDetailsCard
      searchCard
      searchNo={searchNo}
      selected
      compactCard
      getDirectionsHref={openGoogleDirectionMap(
        `${position.lat},${position.lng}`,
      )}
      hideCloseIcon
      i18n={cardText}
      store={{
        id: store.id,
        displayName: store.displayName,
        relativeDistance: store.relativeDistance,
        openingTimes: store.openingTimes,
        address: `${
          store.address.addressLine1 ? `${store.address.addressLine1},` : ''
        } ${
          store.address.addressLine2 ? `${store.address.addressLine2},` : ''
        } ${
          store.address.addressLine3 ? `${store.address.addressLine3},` : ''
        } ${
          store.address.addressLine4 ? `${store.address.addressLine4},` : ''
        } ${store.address.postalCode ? store.address.postalCode : ''}`,
        storeFacility: store.storeFacility,
        status: store.status,
      }}
      storeDetailsHref={storeDetailsHref?.(
        store?.address?.addressLine5 || '',
        store?.address?.addressLine4 || '',
        store?.urlTag,
      )}
    />
  )
}

export const AdvancedMapMarker = ({
  position,
  title,
  counter,
  markerId,
  onMarkerClick,
  selectedMarkerId,
  defaultPinColor,
  selectedPinColor,
  hoverPinColor,
  mapInstance,
  showMarkerInfoWindow,
  customIcon,
  store,
  storeDetailsHref,
  cardText,
}: MarkerProps & {
  counter: number
  onMarkerClick(
    position: MapLocationProps,
    isInfoWindowOpen: boolean,
    markerId: number | null | undefined,
  ): void
  selectedMarkerId?: number | null
  defaultPinColor: PinProps | null
  hoverPinColor: PinProps | null
  selectedPinColor: PinProps | null
  mapInstance?: google.maps.Map | null
  storeDetailsHref?: (
    region: string,
    town: string,
    storeUrlTag: string,
  ) => string
  cardText: StoreDetailsCardI18nText
}): JSX.Element => {
  const [isHovered, setIsHovered] = React.useState<boolean>(false)
  const [isInfoWindowOpen, setIsInfoWindowOpen] = React.useState<boolean>(false)
  const [selectMarker, setSelectMarker] = React.useState<number | null>(null)
  const markerRef = React.useRef<google.maps.marker.AdvancedMarkerElement | null>(
    null,
  )
  const pinElementRef = React.useRef<google.maps.marker.PinElement | null>(null)
  const theme = useTheme()

  const fillColor = isInfoWindowOpen
    ? selectedPinColor?.fillColor
    : isHovered
    ? hoverPinColor?.fillColor || selectedPinColor?.fillColor
    : defaultPinColor?.fillColor

  const textColor = isInfoWindowOpen
    ? selectedPinColor?.textColor
    : isHovered
    ? hoverPinColor?.textColor || selectedPinColor?.textColor
    : defaultPinColor?.textColor

  const getCurrentColors = () => {
    const isActive = isInfoWindowOpen || isHovered
    const fallbackFillColor = isActive
      ? theme.patterns.storeMap.pinFillFallbackColor.active
      : theme.patterns.storeMap.pinFillFallbackColor.inactive
    const fallbackTextColor = isActive
      ? theme.patterns.storeMap.pinTextFallbackColor.active
      : theme.patterns.storeMap.pinTextFallbackColor.inactive

    return {
      background: fillColor || fallbackFillColor,
      glyphColor: textColor || fallbackTextColor,
      borderColor: theme.patterns.storeMap.pinBorderColor,
    }
  }

  const updatePinElement = async () => {
    if (!window.google?.maps) return null

    try {
      const { PinElement } = (await google.maps.importLibrary(
        'marker',
      )) as google.maps.MarkerLibrary

      const colors = getCurrentColors()

      if (customIcon) {
        const imgElement = document.createElement('img')
        imgElement.src = customIcon
        imgElement.style.width = '32px'
        imgElement.style.height = '32px'
        return imgElement
      }

      const createCounterElement = () => {
        const span = document.createElement('span')
        span.textContent = String(counter)
        span.style.color = colors.glyphColor
        span.style.fontSize = '13px'
        return span
      }
      if (!pinElementRef.current) {
        pinElementRef.current = new PinElement({
          background: colors.background,
          glyphColor: colors.glyphColor,
          borderColor: colors.borderColor,
          glyph: createCounterElement(),
        })
      } else {
        pinElementRef.current.background = colors.background
        pinElementRef.current.borderColor = colors.borderColor
        pinElementRef.current.glyphColor = colors.glyphColor
        pinElementRef.current.glyph = createCounterElement()
      }

      return pinElementRef.current.element
    } catch (error) {
      console.error('Error creating pin element:', error)
      return null
    }
  }

  const handleMarkerSelect = () => {
    if (markerId) {
      setSelectMarker(markerId)
      setIsInfoWindowOpen(!isInfoWindowOpen)
      onMarkerClick(position, !isInfoWindowOpen, markerId)
    }
  }

  React.useEffect(() => {
    if (!mapInstance || !window.google?.maps) return

    const createMarker = async () => {
      try {
        const { AdvancedMarkerElement } = (await google.maps.importLibrary(
          'marker',
        )) as google.maps.MarkerLibrary

        const content = await updatePinElement()
        if (!content) return null

        markerRef.current = new AdvancedMarkerElement({
          position,
          title,
          content,
          map: mapInstance,
        })

        if ('gmpClickable' in markerRef.current) {
          ;(markerRef.current as any).gmpClickable = true
        }

        markerRef.current.addEventListener('click', () => handleMarkerSelect())
        markerRef.current.addEventListener('mouseenter', () =>
          setIsHovered(true),
        )
        markerRef.current.addEventListener('mouseleave', () =>
          setIsHovered(false),
        )
      } catch (error) {
        console.error('Error creating advanced marker:', error)
      }
    }

    createMarker()

    return () => {
      if (markerRef.current) {
        markerRef.current.map = null
        markerRef.current = null
      }
    }
  }, [mapInstance, position, title])

  const updatePinStyling = async () => {
    if (markerRef.current && !customIcon) {
      const content = await updatePinElement()
      if (content) {
        markerRef.current.content = content
      }
    }
  }

  // Update pin styling when state changes
  React.useEffect(() => {
    updatePinStyling()
  }, [isHovered, isInfoWindowOpen, selectedMarkerId, counter])

  // Handle external marker selection
  React.useEffect(() => {
    if (!isInfoWindowOpen && mapInstance && selectMarker) {
      setSelectMarker(null)
      onMarkerClick(position, isInfoWindowOpen, markerId)
    }
  }, [isInfoWindowOpen, mapInstance])

  React.useEffect(() => {
    if (
      mapInstance &&
      !selectMarker &&
      Number(selectedMarkerId) === Number(markerId)
    ) {
      handleMarkerSelect()
    }
    if (
      typeof selectedMarkerId === 'number' &&
      Number(selectedMarkerId) !== Number(markerId)
    ) {
      // Clear selectMarker to prevent triggering onMarkerClick callback & zooming out on clicking another marker
      setSelectMarker(null)
      setIsInfoWindowOpen(false)
    }
  }, [selectedMarkerId, mapInstance])

  return (
    <React.Fragment>
      {isInfoWindowOpen && showMarkerInfoWindow && (
        <InfoWindow
          position={position}
          onCloseClick={() => setIsInfoWindowOpen(false)}
          options={{
            minWidth: 375,
            pixelOffset: new window.google.maps.Size(0, -40),
          }}
        >
          <PinDetailsCard
            searchNo={counter}
            position={position}
            store={store}
            storeDetailsHref={storeDetailsHref}
            cardText={cardText}
          />
        </InfoWindow>
      )}
    </React.Fragment>
  )
}
